import { useMemo, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";

import { queryClient } from "App";
import Constants from "constants/Constants";
import DataJobService from "services/DataJob";
import UserService from "services/User";
import TagService from "services/Tag";
import {
  getDataJobTableColumns,
  getDataJobTableFilters,
} from "routes/data_tab/DataJobTableColumnsAndFilters";
import { addResizedColumns } from "./useTableConfig";
import { DATA_JOB_TABLE_CONSTANTS } from "routes/data_tab/DataJobTable";
import { formatUsersForFilter } from "helpers/user";

const { DATA_JOB_STATUSES } = Constants;

export async function invalidateDataJobsQuery() {
  await queryClient.invalidateQueries({
    queryKey: [DATA_JOB_TABLE_CONSTANTS.queryKey],
  });
}

const JOB_STATUSES = Object.keys(DATA_JOB_STATUSES).map(status => ({
  id: DATA_JOB_STATUSES[status],
  name: status.replace(/_/g, " ").toUpperCase(),
}));

async function handleFetchUsers() {
  const rawUsers = await UserService.getUsers();
  return formatUsersForFilter(rawUsers);
}

async function handleFetchJobTypes() {
  const jobTypes = await DataJobService.getJobTypes();
  return jobTypes.map(jobType => ({
    id: jobType,
    name: jobType,
  }));
}

async function handleFetchTags() {
  const tags = await TagService.getTags({
    model: Constants.AUDIT_LOG_CATEGORIES.DATA_JOB,
    for_filtering: true,
  });
  return tags;
}

export function useDataJobTable({ tableName }) {
  const importModal = useRef(null);
  const caseDataImportModal = useRef(null);

  const openImportModal = useCallback(dataJobId => {
    if (importModal.current) {
      importModal.current.open(dataJobId);
    }
  }, []);

  const openCaseDataImportModal = useCallback(dataJobId => {
    if (caseDataImportModal.current) {
      caseDataImportModal.current.open(dataJobId);
    }
  }, []);

  const { data: users, isSuccess: isUsersFetched } = useQuery({
    queryKey: ["dataJobUsers"],
    queryFn: handleFetchUsers,
  });

  const { data: types, isSuccess: isTypesFetched } = useQuery({
    queryKey: ["dataJobTypes"],
    queryFn: handleFetchJobTypes,
  });

  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", tableName],
    queryFn: handleFetchTags,
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings", tableName],
    queryFn: () => UserService.getUserSettings(tableName),
  });

  const filters = useMemo(() => {
    if (!isUsersFetched || !isTypesFetched || !isTagsFetched) {
      return [];
    }

    return getDataJobTableFilters({
      statuses: JOB_STATUSES || [],
      users: users || [],
      types: types || [],
      tags: tags || [],
    });
  }, [users, types, tags, isUsersFetched, isTypesFetched, isTagsFetched]);

  const dataJobTableSettings = useMemo(() => {
    if (!isTableSettingsFetched) return {};
    return get(
      tableSettings,
      `user_settings.preferences.tables.${DATA_JOB_TABLE_CONSTANTS.tableName}`,
      {}
    );
  }, [tableSettings, isTableSettingsFetched]);

  const deleteJob = useCallback(async id => {
    await DataJobService.deleteJob(id);
    invalidateDataJobsQuery();
  }, []);

  const columns = useMemo(() => {
    // this will only be called at an event handler
    // eslint-disable-next-line react-hooks/refs
    const initialColumns = getDataJobTableColumns({
      onDeleteJob: deleteJob,
      onOpenImportModal: openImportModal,
      onOpenCaseDataImportModal: openCaseDataImportModal,
      invalidateData: invalidateDataJobsQuery,
      className: DATA_JOB_TABLE_CONSTANTS.className,
    });

    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: dataJobTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    deleteJob,
    openImportModal,
    openCaseDataImportModal,
    dataJobTableSettings,
  ]);

  return {
    filters,
    columns,
    tags,
    invalidateDataJobsQuery,
    openImportModal,
    importModal,
    openCaseDataImportModal,
    caseDataImportModal,
    deleteJob,
  };
}
